import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import LoadingSequence from '../components/LoadingSequence';
import Hero from '../components/Hero';
import Navigation from '../components/Navigation';

// Lazy load components
const About = lazy(() => import('../components/About'));
const Founder = lazy(() => import('../components/Founder'));
const Services = lazy(() => import('../components/Services'));
const Portfolio = lazy(() => import('../components/Portfolio'));
const Blog = lazy(() => import('../components/Blog'));
const MediaPartners = lazy(() => import('../components/MediaPartners'));
const Contact = lazy(() => import('../components/Contact'));
const Footer = lazy(() => import('../components/Footer'));

// Fallback for Suspense
const SectionLoader = () => (
  <div className="h-screen flex items-center justify-center bg-background">
    <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
  </div>
);

const Index = () => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    if (!isMobile) {
      // Initialize Lenis for smooth scrolling on non-mobile
      const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });

      lenisRef.current = lenis;

      function raf(time: number) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      // Return cleanup function only if Lenis was initialized
      return () => {
        lenis.destroy();
      };
    }
    // No explicit return is needed here for the mobile case, useEffect implicitly returns undefined
  }, []);

  return (
    <div className="bg-gray-950 text-white overflow-x-hidden">
      <LoadingSequence />
      <Navigation />
      <main>
        <Hero />
        <Suspense fallback={<SectionLoader />}>
          <About />
          <Founder />
          <Services />
          <Portfolio />
          <Blog />
          <MediaPartners />
          <Contact />
        </Suspense>
      </main>
      <Suspense fallback={<div className="h-32 bg-background" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
